import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { Header } from "./componentes/header";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { useMemo } from "react";
import { GenerateGedColumnDefinitions } from "./columns";
import { GEDDocumento } from "../../../../../common/domain/models/ged_documento";
import { CadastroDocumentoModal } from "./componentes/cadastro_documento_modal";
import { ChangeHistoryModal } from "../../../../../common/modals/change_history_modal";
import { CopyDocumentoModal } from "./componentes/copy_documento_modal";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { useGedEquipamentoMedicoesViewModel } from "../../viewModels/ged_equipamento_medicoes_view_model";

import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";
import RefreshButton from "../../../../../common/components/refresh_button";
import AlertError from "../../../../../common/components/alert_error";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * GedEquipamentoMedicoes
 */

export const GedEquipamentoMedicoes = () => {
  const viewModel = useGedEquipamentoMedicoesViewModel();

  const columnsDef = useMemo(() => {
    return GenerateGedColumnDefinitions({
      onActionEdit: viewModel.handleCadastroDocumentoOpenState,
      onActionDelete: viewModel.handleDeleteDocumentoOpenState,
      onActionCopy: viewModel.handleCopyDocumentoOpenState,
      onActionVisualize: viewModel.handleActionVisualize,
      onActionDownload: viewModel.handleActionDownload,
    });
  }, [viewModel.handleCadastroDocumentoOpenState, viewModel.handleDeleteDocumentoOpenState, viewModel.handleCopyDocumentoOpenState, viewModel.handleActionVisualize, viewModel.handleActionDownload]);

  return (
    <>
      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="w-[160px]">
              <PrimaryButton
                startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                onClick={() => viewModel.handleCadastroDocumentoOpenState(null, true)}>
                Novo Documento
              </PrimaryButton>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            Equipamento de Medições
          </Link>
          <Typography className="text-primary-green">
            Gerenciar Documentos
          </Typography>
        </Breadcrumbs>

        <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
          <div className="flex gap-2 items-center w-full">
            <h1 className="text-3xl font-medium text-black dark:text-white">Gerenciar Documentos</h1>
            <RefreshButton onClick={viewModel.handleReload} />
          </div>

          <div className="w-full justify-end flex gap-2"></div>
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload} />
              </div>
            ) : (
              <>
                <Header
                  tipoPesquisa={viewModel.filters.tipoPesquisa}
                  valueField={viewModel.filters.valueField}
                  status={viewModel.filters.status}
                  usuarios={viewModel.filters.usuariosDropdown ?? []}
                  onTipoPesquisaChange={viewModel.handleTipoPesquisaChange}
                  onValueFieldChange={viewModel.handleValueFieldChange}
                  onStatusChange={viewModel.handleStatusChange}
                  onClearFilters={viewModel.handleClearFilters} />

                <Stack spacing={0}>
                  <div className="w-full flex flex-row justify-between items-stretch">
                    <div className="flex flex-col justify-between flex-1">
                      <TablePagination
                        component="div"
                        count={viewModel.pagination.totalItems}
                        page={viewModel.pagination.page - 1}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        onPageChange={(_, p) => viewModel.handlePageChange(p)}
                        rowsPerPage={viewModel.pagination.rowsPerPage}
                        onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />
                    </div>
                  </div>

                  <StyledDataGrid
                    tableName="ged_equipamento_medicoes"
                    disableRowSelectionOnClick={true}
                    checkboxSelection={false}
                    disableColumnMenu={false}
                    rowSelectionModel={viewModel.rowSelectionModel}
                    // onRowSelectionModelChange={viewModel.handleRowSelectionModel}
                    getRowId={(row: GEDDocumento) => row.idArquivoModulo}
                    columns={columnsDef}
                    loading={viewModel.loading}
                    rows={viewModel.documentos}
                    rowCount={viewModel.documentos.length}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }} />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage > 0 ? viewModel.pagination.rowsPerPage : 25}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />

                </Stack>
              </>
            )
        }

        {/**
          * Modals
          */}

        <CadastroDocumentoModal
          open={viewModel.openCadastroModal}
          saving={viewModel.saving}
          selectedDocumento={viewModel.selectedDocumento}
          onClose={() => viewModel.handleCadastroDocumentoOpenState(null, false)}
          onSubmit={viewModel.handleDocumentoSubmit}
          onDownloadFile={viewModel.handleDownloadFile}
          onDownloadFilePdf={viewModel.handleDownloadFilePdf}
          onChangeHistory={() => viewModel.handleOpenChangeHistory()} />

        <CopyDocumentoModal
          open={viewModel.openCopyModal}
          saving={viewModel.saving}
          selectedDocumento={viewModel.selectedDocumento}
          onClose={() => viewModel.handleCopyDocumentoOpenState(null, false)}
          onSubmit={viewModel.handleDocumentoCopySubmit}
          onDownloadFile={viewModel.handleDownloadFile}
          onDownloadFilePdf={viewModel.handleDownloadFilePdf} />

        <DeleteDialog
          open={viewModel.openDeleteModal}
          saving={viewModel.saving}
          onClose={() => viewModel.handleDeleteDocumentoOpenState(null, false)}
          title={`Você realmente deseja excluir este item?`}
          onDelete={viewModel.handleConfirmarDeleteDocumento} />

        {/**
          * Modal historico de alterações.
          */}

        <ChangeHistoryModal
          changeHistoryHandler={viewModel.changeHistory} />

      </div>
    </>
  );
}